// #766 Story E View 4 — deck composition health (variety regression).
//
// Shannon entropy of dealt card_keys over the last N calls per user.
// Population mode: median + p10/p90 entropy across users + list of
// users below the LOW_VARIETY_ENTROPY_THRESHOLD (1.5).
// Per-user mode (via ?user_id=): 12-week trajectory + current entropy.
//
// Inline SVG charts (no chart-library dep — matches AdminUserEngagement
// approach from #765).
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

function entropyColor(e, threshold) {
  if (e == null) return '#aaa';
  if (e < threshold) return '#DC3545';        // below threshold — red
  if (e < threshold + 0.5) return '#E8845A';  // borderline — coral
  return '#28A745';                            // healthy — green
}

function fmtWeek(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Mini SVG trajectory chart for per-user variety over weeks.
function VarietyTrajectoryChart({ weeks, threshold }) {
  if (!weeks || weeks.length === 0) {
    return <div className="empty-state">No data yet.</div>;
  }
  const W = 600, H = 160, P = 30;
  // Y range: 0 to max(observed, 4) — entropy can climb high with many card_types.
  const maxE = Math.max(4, ...weeks.map(w => w.entropy || 0));
  const xs = weeks.map((_, i) =>
    P + (i * (W - 2 * P)) / Math.max(1, weeks.length - 1)
  );
  const yFor = v => H - P - (Math.max(0, Math.min(maxE, v)) / maxE) * (H - 2 * P);
  const points = xs
    .map((x, i) => weeks[i].entropy == null ? null : `${x.toFixed(1)},${yFor(weeks[i].entropy).toFixed(1)}`)
    .filter(Boolean)
    .join(' ');
  const threshY = yFor(threshold);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{
      background: '#FAF8F5', borderRadius: 8,
    }}>
      {/* Low-variety threshold line. */}
      <line x1={P} y1={threshY} x2={W - P} y2={threshY}
            stroke="#DC3545" strokeWidth="1" strokeDasharray="3,3" />
      <text x={W - P + 4} y={threshY + 3} fill="#DC3545" fontSize="10">
        {threshold}
      </text>
      {points && (
        <polyline fill="none" stroke="#E8845A" strokeWidth="2" points={points} />
      )}
      {weeks.map((w, i) => w.entropy != null && (
        <circle key={i} cx={xs[i]} cy={yFor(w.entropy)} r="3"
                fill={entropyColor(w.entropy, threshold)} />
      ))}
      {weeks.length > 0 && (
        <>
          <text x={xs[0]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
            {fmtWeek(weeks[0].week_start)}
          </text>
          {weeks.length > 2 && (
            <text x={xs[Math.floor(weeks.length / 2)]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
              {fmtWeek(weeks[Math.floor(weeks.length / 2)].week_start)}
            </text>
          )}
          {weeks.length > 1 && (
            <text x={xs[xs.length - 1]} y={H - 10} fill="#888" fontSize="10" textAnchor="middle">
              {fmtWeek(weeks[weeks.length - 1].week_start)}
            </text>
          )}
        </>
      )}
    </svg>
  );
}

export default function AdminCardsVariety() {
  const [params, setParams] = useSearchParams();
  const userId = params.get('user_id');
  const window = parseInt(params.get('window') || '5', 10) || 5;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (userId) qs.set('user_id', userId);
        if (window) qs.set('window', String(window));
        const r = await adminFetch(`/api/admin/cards/variety?${qs}`);
        if (cancelled) return;
        setData(r || {});
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, window]);

  const isPerUser = !!userId;
  const threshold = data?.threshold ?? 1.5;

  return (
    <AdminLayout title="Cards · Variety">
      <style>{userSubPageStyles}</style>

      <div className="page-header">
        <div className="page-title">
          Variety regression<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Shannon entropy of dealt card_keys · {isPerUser ? `per-user (${userId})` : 'population'} ·
          {' '}window = last {window} calls · low-variety threshold {threshold} bits · #766 Story E View 4
        </div>
      </div>

      <div style={{ marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
        <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600 }}>Window (last N calls):</label>
        <input
          type="number" min="1" max="50" value={window}
          onChange={e => {
            const v = parseInt(e.target.value || '5', 10) || 5;
            const next = new URLSearchParams(params);
            next.set('window', String(v));
            setParams(next);
          }}
          style={{ width: 80, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
        />
        {isPerUser && (
          <Link to="/admin/cards/variety" style={{ fontSize: '0.85rem', color: '#E8845A' }}>
            ← Back to population view
          </Link>
        )}
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && data && isPerUser && (
        <>
          <div className="section-title">Current entropy
            <span className="section-sub">
              over last {data.window} calls · low-variety flag fires below {threshold} bits
            </span>
          </div>
          <div style={{
            background: '#FAF8F5', borderRadius: 8, padding: '20px 24px',
            marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20,
          }}>
            <div style={{
              fontSize: '2.4rem', fontWeight: 700,
              color: entropyColor(data.current_entropy, threshold),
            }}>
              {data.current_entropy != null ? data.current_entropy.toFixed(2) : '—'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                {data.low_variety
                  ? <strong style={{ color: '#DC3545' }}>⚠ Low variety — dealer has collapsed onto too few card types.</strong>
                  : <span>Healthy variety.</span>}
              </div>
              <div style={{ color: '#999', fontSize: '0.82rem', marginTop: 6 }}>
                Higher entropy = more even distribution of card types.
                Below {threshold} bits suggests Story B's preference weights
                may have over-converged; consider tuning decay or seeding fresh cards.
              </div>
            </div>
          </div>

          <div className="section-title">12-week trajectory
            <span className="section-sub">weekly entropy across all calls in the week</span>
          </div>
          <VarietyTrajectoryChart weeks={data.weeks || []} threshold={threshold} />

          <div className="section-title" style={{ marginTop: 24 }}>Per-week breakdown</div>
          <div className="data-grid">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Week of</th>
                  <th>Entropy (bits)</th>
                  <th>Cards dealt</th>
                </tr>
              </thead>
              <tbody>
                {(data.weeks || []).length === 0 && (
                  <tr><td colSpan={3} className="empty-state">No data yet.</td></tr>
                )}
                {(data.weeks || []).map((w, i) => (
                  <tr key={w.week_start || i}>
                    <td>{fmtWeek(w.week_start)}</td>
                    <td style={{
                      fontWeight: 600, color: entropyColor(w.entropy, threshold),
                    }}>
                      {w.entropy != null ? w.entropy.toFixed(2) : '—'}
                    </td>
                    <td>{w.n_dealt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && data && !isPerUser && (
        <>
          <div className="section-title">Population aggregate
            <span className="section-sub">entropy per user over last {data.window} calls · n = {data.population?.n_users ?? 0} users</span>
          </div>
          <div style={{
            display: 'flex', gap: 18, marginBottom: 24, flexWrap: 'wrap',
          }}>
            {[
              ['Median', data.population?.median],
              ['p10', data.population?.p10],
              ['p90', data.population?.p90],
            ].map(([label, val]) => (
              <div key={label} style={{
                background: '#FAF8F5', borderRadius: 8, padding: '16px 24px', minWidth: 120,
              }}>
                <div style={{ color: '#888', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{
                  fontSize: '1.6rem', fontWeight: 700, marginTop: 4,
                  color: entropyColor(val, threshold),
                }}>
                  {val != null ? val.toFixed(2) : '—'}
                </div>
              </div>
            ))}
          </div>

          <div className="section-title">Users below threshold ({threshold} bits)
            <span className="section-sub">
              {data.users_below_threshold?.length ?? 0} users · click to drill in
            </span>
          </div>
          <div className="data-grid">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Current entropy</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data.users_below_threshold || []).length === 0 ? (
                  <tr><td colSpan={3} className="empty-state">No users below threshold.</td></tr>
                ) : (
                  data.users_below_threshold.map(u => (
                    <tr key={u.user_id}>
                      <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}>{u.user_id}</td>
                      <td style={{ fontWeight: 600, color: '#DC3545' }}>{u.entropy.toFixed(2)}</td>
                      <td>
                        <Link to={`/admin/cards/variety?user_id=${u.user_id}`}
                              style={{ color: '#E8845A', fontSize: '0.85rem' }}>
                          drill in →
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
